import { useEffect, useState } from "react";
import { Wrench } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { btn, field } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";

const Lbl = ({ t, children }) => <label className="block"><span className="mb-1 block font-mono text-[10px] uppercase text-slate-500">{t}</span>{children}</label>;
const In = ({ v, set, id, ...p }) => <Input value={v ?? ""} onChange={(e) => set(e.target.value)} className={`h-8 ${field}`} data-testid={id} {...p} />;
const Sec = ({ t, children }) => <div className="space-y-2 rounded-sm border border-slate-800 p-3"><div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t}</div>{children}</div>;

const PriceInputs = ({ fix, sap, val, set, id }) => fix.price_mode === "breaks" ? (
  <div className="flex flex-wrap gap-2">
    {fix.breaks.map((b, i) => <Lbl key={b} t={b}><In v={(val.breaks || [])[i]} set={(x) => { const a = [...(val.breaks || fix.breaks.map(() => ""))]; a[i] = x; set({ ...val, breaks: a }); }} id={`${id}-break-${i}`} className={`h-8 w-24 ${field}`} /></Lbl>)}
    <Lbl t="Ex-works (under first break)"><In v={val.exw} set={(x) => set({ ...val, exw: x })} id={`${id}-exw`} className={`h-8 w-24 ${field}`} /></Lbl>
    {!sap && <span className="self-end text-xs text-slate-500">uses the SAP material</span>}
  </div>
) : <Lbl t={`Price (${fix.currency})`}><In v={val.price} set={(x) => set({ ...val, price: x })} id={`${id}-price`} className={`h-8 w-28 ${field}`} /></Lbl>;

const ItemRow = ({ fix, it, v, set, products }) => (
  <div className="space-y-2 border-b border-slate-800/60 pb-3" data-testid={`fix-item-${it.code}`}>
    <div className="text-sm"><span className="font-mono text-slate-200">{it.code}</span> {it.gtin && <span className="font-mono text-xs text-slate-500">GTIN {it.gtin}</span>} <span className="text-slate-400">{it.description}</span></div>
    <div className="flex gap-3 text-xs">
      {["new", "link"].map((m) => <label key={m} className="flex items-center gap-1"><input type="radio" checked={v.mode === m} onChange={() => set({ ...v, mode: m })} data-testid={`fix-item-${it.code}-${m}`} />{m === "new" ? "New product" : "Link to an existing product"}</label>)}
    </div>
    {v.mode === "link" ? (
      <select value={v.part_no_link || ""} onChange={(e) => set({ ...v, part_no_link: e.target.value })} className={`h-9 w-full rounded-md border px-2 text-sm ${field}`} data-testid={`fix-item-${it.code}-pick`}>
        <option value="">Pick product…</option>{products.map((p) => <option key={p.part_no} value={p.part_no}>{p.part_no} - {p.ep_sku} - {p.description}</option>)}
      </select>
    ) : (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
        {[["part_no", "Part no."], ["ep_sku", "SAP material"], ["description", "Description"], ["uom", "Unit"], ["upc", "GTIN / EAN"], ["units_per_pallet", "Per pallet"]].map(([k, l]) => (
          <Lbl key={k} t={l}><In v={v[k]} set={(x) => set({ ...v, [k]: x })} id={`fix-item-${it.code}-${k}`} /></Lbl>))}
      </div>
    )}
    {(v.mode === "new" || (v.mode === "link" && v.part_no_link)) && <PriceInputs fix={fix} sap={v.mode === "new" ? v.ep_sku : products.find((p) => p.part_no === v.part_no_link)?.ep_sku} val={v} set={set} id={`fix-item-${it.code}`} />}
  </div>
);

const init = (fix) => ({
  sold_to: "", dp: fix.delivery_point ? { ...fix.delivery_point } : null,
  items: Object.fromEntries(fix.items.map((i) => [i.code, { mode: "new", part_no: i.code, ep_sku: "", description: i.description, uom: i.uom, upc: i.gtin, units_per_pallet: "" }])),
  prices: Object.fromEntries(fix.prices.map((p) => [p.part_no, {}])), sap: {}, upp: {},
});

const payload = (fix, s, products) => {
  const b = { partner: fix.partner, source: fix.source || "", sold_to: s.sold_to, new_items: [], links: [], prices: {}, break_prices: {}, ex_works: {}, sap_materials: s.sap, units_per_pallet: s.upp };
  if (s.dp && !String(s.dp.sap_ship_to || "").trim()) throw new Error("Enter the SAP ship-to number for the delivery point - without it the PO stays stopped");
  if (s.dp && !String(s.dp.gln || "").trim() && !String(s.dp.depot_code || "").trim()) throw new Error("Enter the GLN or Depot / FC code for the delivery point");
  if (fix.sold_to && !String(s.sold_to || "").trim()) throw new Error("Enter the SAP sold-to number");
  if (s.dp) { const { exists, ...dp } = s.dp; b.delivery_point = dp; }
  const addPrice = (part, sap, v) => {
    if (fix.price_mode === "breaks") {
      if ((v.breaks || []).some((x) => x !== "" && x != null)) b.break_prices[sap] = fix.breaks.map((_, i) => (v.breaks || [])[i] ?? "");
      if (v.exw) b.ex_works[sap] = v.exw;
    } else if (v.price) b.prices[part] = v.price;
  };
  Object.entries(s.items).forEach(([code, v]) => {
    if (v.mode === "link") { if (v.part_no_link) { b.links.push({ part_no: v.part_no_link, customer_item: code }); addPrice(v.part_no_link, products.find((p) => p.part_no === v.part_no_link)?.ep_sku, v); } return; }
    if (!v.ep_sku) throw new Error(`New product ${code}: enter the SAP material (or link it to an existing product)`);
    b.new_items.push({ ...v, customer_item: code });
    addPrice(v.part_no, v.ep_sku, v);
  });
  fix.prices.forEach((p) => addPrice(p.part_no, p.sap, s.prices[p.part_no] || {}));
  return b;
};

const FixDialog = ({ fix, close, done }) => {
  const { user } = useAuth();
  const mgr = user?.role === "manager";
  const [s, setS] = useState(() => init(fix));
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/items").then((r) => setProducts(r.data.filter((i) => i.type !== "RM"))); }, []);
  const up = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    let body;
    try { body = payload(fix, s, products); } catch (e) { toast.error(e.message); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/po-fix", body);
      toast.success(`Saved: ${data.saved.join("; ")}`);
      close();
      done();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const dpF = [["gln", "GLN"], ["depot_code", "Depot / FC code"], ["sap_ship_to", "SAP ship-to *"], ["name", "Name"], ["street", "Street"], ["street2", "Street 2"], ["city", "City"], ["county", "County"], ["postcode", "Postcode"]];
  return (
    <Dialog open onOpenChange={close}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto border-slate-800 bg-[#111317] text-slate-100" data-testid="fix-dialog">
        <DialogHeader><DialogTitle>Fix missing details - {fix.partner_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">Taken from the PO and editable. Saving updates the Item Master and {fix.partner_name}'s settings straight away, then the PO is re-checked.</p>
        {!mgr && <p className="text-sm text-amber-300" data-testid="fix-readonly">Only a manager can save these details - ask a manager to complete them.</p>}
        <fieldset disabled={!mgr} className="space-y-3">
        {fix.sold_to && <Sec t="SAP sold-to"><Lbl t={`${fix.partner_name} sold-to number`}><In v={s.sold_to} set={(v) => up("sold_to", v)} id="fix-sold-to" /></Lbl></Sec>}
        {s.dp && <Sec t={s.dp.exists ? "Delivery point - add the SAP ship-to" : "New delivery point"}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{dpF.map(([k, l]) => <Lbl key={k} t={l}><In v={s.dp[k]} set={(v) => up("dp", { ...s.dp, [k]: v })} id={`fix-dp-${k}`} /></Lbl>)}</div>
        </Sec>}
        {fix.items.length > 0 && <Sec t="Products not in the Item Master">{fix.items.map((it) => <ItemRow key={it.code} fix={fix} it={it} products={products} v={s.items[it.code]} set={(v) => up("items", { ...s.items, [it.code]: v })} />)}</Sec>}
        {fix.sap_missing.length > 0 && <Sec t="SAP material number missing">{fix.sap_missing.map((p) => <Lbl key={p} t={p}><In v={s.sap[p]} set={(v) => up("sap", { ...s.sap, [p]: v })} id={`fix-sap-${p}`} /></Lbl>)}</Sec>}
        {fix.upp_missing.length > 0 && <Sec t="Units per pallet missing">{fix.upp_missing.map((p) => <Lbl key={p} t={p}><In v={s.upp[p]} set={(v) => up("upp", { ...s.upp, [p]: v })} id={`fix-upp-${p}`} /></Lbl>)}</Sec>}
        {fix.prices.length > 0 && <Sec t="Missing prices">{fix.prices.map((p) => (
          <div key={p.part_no} data-testid={`fix-price-${p.part_no}`}><div className="text-sm"><span className="font-mono">{p.part_no}</span> <span className="text-xs text-slate-500">SAP {p.sap} {p.description}</span></div>
            <PriceInputs fix={fix} sap={p.sap} val={s.prices[p.part_no] || {}} set={(v) => up("prices", { ...s.prices, [p.part_no]: v })} id={`fix-price-${p.part_no}`} /></div>))}</Sec>}
        </fieldset>
        <Button onClick={save} disabled={!mgr || busy} className={btn.primary} data-testid="fix-save-btn">{busy ? "Saving…" : "Save and re-check"}</Button>
      </DialogContent>
    </Dialog>
  );
};

export const FixButton = ({ fix, onDone, testId = "fix-open-btn", small }) => {
  const [open, setOpen] = useState(false);
  if (!fix) return null;
  return (
    <>
      <Button size={small ? "sm" : "default"} variant="ghost" onClick={() => setOpen(true)} className={`${btn.ghost} text-amber-300`} data-testid={testId}><Wrench className="mr-1.5 h-3.5 w-3.5" /> Fix missing details</Button>
      {open && <FixDialog fix={fix} close={() => setOpen(false)} done={onDone} />}
    </>
  );
};
